import React from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
} from "@mui/material";
import DatePicker from "react-datepicker";
import {
  formatAmount,
  formatHistoryAmount,
} from "../../utils/hooks/dataHandler";
import { CalculatorTextField } from "./TextField/NumericTextField";
import { Box } from "@mui/system";
import DecimalTextField from "./TextField/DecimalTextField";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IconButtonWithTooltip from "./IconButtonWithTooltip";
import { YearPicker } from "./Form/YearPicker";
import { TextFieldForViewOnly } from "./TextField/TextField";

const JustTable = ({
  canEdit = true,
  headers,
  rows,
  selectedYears,
  columnSettings,
  dropdownOptions,
  handleInputChange,
  handleDropdownChange,
  handleCheckboxChange,
  handleAddRow,
  handleDeleteRow,
  taxAdjustmentAction,
  children,
  actionColumnWidth,
  tableSx = {},
  containerSx = {},
}) => {
  const getWidthSx = (column = {}) => {
    const width = column.width;
    const minWidth = column.minWidth || width;
    const maxWidth = column.maxWidth || width;

    return {
      ...(width ? { width } : {}),
      ...(minWidth ? { minWidth } : {}),
      ...(maxWidth ? { maxWidth } : {}),
    };
  };

  const actionWidthSx = getWidthSx({ width: actionColumnWidth });

  return (
    <TableContainer
      component={Paper}
      sx={{
        overflowX: "auto",
        ...containerSx,
      }}
    >
      <Table sx={tableSx}>
        <TableHead>
          <TableRow>
            {headers.map((header, index) => {
              const column = columnSettings?.[index] || {};

              return (
                <TableCell
                  key={`${header.value}-${index}`}
                  align={header.align}
                  sx={getWidthSx({ ...column, ...header })}
                >
                  {header.value}
                </TableCell>
              );
            })}
            {canEdit && (handleAddRow || handleDeleteRow) && (
              <TableCell align='center' sx={actionWidthSx}>
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows &&
            rows.map((row, r_index) => (
              <TableRow key={r_index}>
                {columnSettings.map((column) => {
                  if (row[column.id] != null) {
                    let columnType = column.type;
                    if (row[column.id].justView) {
                      columnType = "";
                    }

                    if (!canEdit) {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align ? column.align : "left"}
                          sx={getWidthSx(column)}
                        >
                          {columnType === "checkbox" ? (
                            <TextFieldForViewOnly
                              value={row[column.id].value ? "Yes" : "No"}
                              sx={{
                                textAlign: "center",
                              }}
                            />
                          ) : (
                            <TextFieldForViewOnly
                              value={row[column.id].value}
                              sx={{
                                textAlign: column.align ? column.align : "left",
                              }}
                            />
                          )}
                        </TableCell>
                      );
                    }

                    if (columnType === "checkbox") {
                      return (
                        <TableCell
                          key={column.id}
                          align='center'
                          sx={getWidthSx(column)}
                        >
                          <Checkbox
                            checked={row[column.id].value}
                            onChange={() =>
                              handleCheckboxChange(r_index, column.id)
                            }
                          />
                        </TableCell>
                      );
                    } else if (columnType === "text") {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align ? column.align : "left"}
                          sx={getWidthSx(column)}
                        >
                          <TextField
                            value={row[column.id].value}
                            onChange={(e) =>
                              handleInputChange(
                                r_index,
                                column.id,
                                e.target.value
                              )
                            }
                            size='small'
                            fullWidth={column.fullWidth}
                            disabled={
                              row[column.id]["disabled"] || column.disabled
                            }
                            inputProps={{
                              style: {
                                textAlign: column.align ? column.align : "left",
                              },
                            }}
                          />
                        </TableCell>
                      );
                    } else if (columnType === "number_text") {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align ? column.align : "left"}
                          sx={getWidthSx(column)}
                        >
                          <Tooltip
                            title={
                              row[column.id].historyAmount &&
                              row[column.id].historyAmount.length > 0 &&
                              formatHistoryAmount(row[column.id].historyAmount)
                            }
                            placement='top'
                          >
                            <Box display='inline-block'>
                              <CalculatorTextField
                                value={row[column.id].historyAmount || []}
                                onChange={(value) =>
                                  handleInputChange(r_index, column.id, value)
                                }
                                disabled={row[column.id]["disabled"]}
                              />
                            </Box>
                          </Tooltip>
                        </TableCell>
                      );
                    } else if (columnType === "number_decimal") {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align ? column.align : "left"}
                          sx={{
                            width: column.width || "120px",
                            minWidth: column.minWidth || column.width || "120px",
                            maxWidth: column.maxWidth || column.width || "120px",
                          }}
                        >
                          <DecimalTextField
                            value={row[column.id].value}
                            onChange={(value) =>
                              handleInputChange(r_index, column.id, value)
                            }
                            decimalPlaces={4}
                            maxValue={100}
                            warningMessage='Value is limited 100 and 4 decimal places'
                          />
                        </TableCell>
                      );
                    } else if (columnType === "year_text") {
                      return (
                        <TableCell
                          key={column.id}
                          sx={{
                            width: column.width || "100px",
                            minWidth: column.minWidth || column.width || "100px",
                            maxWidth: column.maxWidth || column.width || "100px",
                          }}
                        >
                          <YearPicker
                            year={row[column.id].value}
                            onChange={(date) =>
                              handleInputChange(r_index, column.id, date)
                            }
                            customMaxDate={
                              new Date(new Date().setFullYear(column.maxYear))
                            }
                            disabled={row[column.id]["disabled"]}
                            style={
                              column.inputWidth
                                ? { width: column.inputWidth }
                                : undefined
                            }
                          />
                          {/* <DatePicker
                            selected={
                              row[column.id].value
                                ? new Date(row[column.id].value, 0)
                                : null
                            }
                            onChange={(date) =>
                              handleInputChange(
                                r_index,
                                column.id,
                                date.getFullYear()
                              )
                            }
                            maxDate={
                              new Date(new Date().setFullYear(column.maxYear))
                            }
                            showYearPicker
                            dateFormat='yyyy'
                            customInput={
                              <TextField
                                fullWidth
                                value={""}
                                size='small'
                                inputProps={{
                                  autoComplete: "off",
                                  readOnly: true,
                                }}
                              />
                            }
                            filterDate={(date) =>
                              selectedYears &&
                              !selectedYears.includes(date.getFullYear())
                            }
                            disabled={row[column.id]["disabled"]}
                          /> */}
                        </TableCell>
                      );
                    } else if (columnType === "dropdown") {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align ? column.align : "left"}
                          sx={getWidthSx(column)}
                        >
                          <FormControl
                            sx={{
                              minWidth: 150,
                            }}
                            size='small'
                          >
                            <Select
                              value={row[column.id].value}
                              onChange={(e) =>
                                handleDropdownChange(
                                  r_index,
                                  column.id,
                                  e.target.value
                                )
                              }
                            >
                              {dropdownOptions.map((option, index) => {
                                let value =
                                  typeof option === "object"
                                    ? option.value
                                    : option;
                                const label =
                                  typeof option === "object"
                                    ? option.label
                                    : option;

                                if (label === "None") value = "";

                                return (
                                  <MenuItem key={index} value={value}>
                                    {label}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </TableCell>
                      );
                    } else if (columnType === "number") {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align ? column.align : "left"}
                          sx={{
                            minWidth: 120,
                            ...getWidthSx(column),
                          }}
                        >
                          {formatAmount(row[column.id].value)}
                        </TableCell>
                      );
                    } else {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align ? column.align : "left"}
                          sx={getWidthSx(column)}
                        >
                          {row[column.id].value}
                        </TableCell>
                      );
                    }
                  } else {
                    return (
                      <TableCell key={column.id} sx={getWidthSx(column)} />
                    );
                  }
                })}
                {taxAdjustmentAction ||
                ((row.permitToAdd || row.permitToDelete) &&
                  (handleAddRow || handleDeleteRow) &&
                  canEdit) ? (
                  <TableCell align='center' sx={actionWidthSx}>
                    {taxAdjustmentAction &&
                      row.taxAdjustment &&
                      taxAdjustmentAction(
                        row.taxAdjustment?.sch,
                        row.taxAdjustment.value,
                        r_index
                      )}
                    {row.permitToAdd && handleAddRow && (
                      <Button
                        variant='outlined'
                        color='primary'
                        size='small'
                        onClick={handleAddRow}
                        style={{ width: "60px" }}
                      >
                        Add
                      </Button>
                    )}
                    {row.permitToDelete && handleDeleteRow && (
                      <IconButtonWithTooltip
                        onClick={() => handleDeleteRow(r_index)}
                        icon={DeleteOutlineIcon}
                        tooltip='Remove Row'
                        color='error'
                        size='small'
                      />
                    )}
                  </TableCell>
                ) : canEdit ? (
                  <TableCell sx={actionWidthSx}></TableCell>
                ) : (
                  <></>
                )}
              </TableRow>
            ))}
          {children}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default JustTable;
